import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpWrapperService } from '../http-wrapper.service';
import { IHttpRequestOptions } from '../../interfaces/http-request-option.interface';
import { catchError } from 'rxjs/operators';
import {
  CampaignTemplateBase,
  CampaignTemplateModel,
  ICampaign,
  ICampaignDetailModel,
  ICampaignDto,
  ICampaignCardItem,
  ICamapignListViewModel,
  ICampaignBase,
  ICampaignItemDto,
  IGroupedCampaignItem,
  ILinkedInitiativeModel,
  ILinkedCampaignListDto,
} from '../../interfaces/campaign.interface';
import { CampaignTypeEnum } from '../../interfaces/common.interfaces';
import { IPagingRequestModel } from '../../interfaces/IPagingRequestModel.interface';
import { PagedResult } from '../../interfaces/IPagingResponseModel.interface';
import { IContributor } from '../../interfaces/contributor.interface';
import { HttpHeaders } from '@angular/common/http';
import { IFormOverviewModel } from '../../interfaces/items.interface';

@Injectable({
  providedIn: 'root',
})
export class CampaignService {
  constructor(private httpWrapperService: HttpWrapperService) {}
  campaignTypeEnum = CampaignTypeEnum;
  // getCampaignsBasedOnIds(
  //   communityId: number,
  //   campaignIds: number[],
  // ): Observable<ICampaign[]> {
  //   const url = `Campaign/list/${communityId}`;
  //   const options: IHttpRequestOptions = { url: url };
  //   options.body = campaignIds;
  //   return this.httpWrapperService
  //     .postFromApiGateWay(options)
  //     .pipe(map((response: ICampaign[]) => response));
  // }

  getUserCampaigns(
    userId: number
  ): Observable<ICampaign[]> {
    const url = `Campaign/user/campaigns/${userId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response: ICampaign[]) => response));
  }

 getCampaignsByCommunityId(
  communityId: number,
  request: IPagingRequestModel,
): Observable<{
  items: ICampaignCardItem[];
  paging: PagedResult<ICampaignCardItem>['paging'];
  campaignTypePresent: Record<number, boolean>;
  hasPrivateCampaign: boolean;
}> {
  const url = `campaign/lists/${communityId}`;
  const options: IHttpRequestOptions = {
    url: url,
    body: request,
  };

  return this.httpWrapperService.postFromApiGateWay(options).pipe(
    map((response: { 
      pagedResult: PagedResult<ICamapignListViewModel>, 
      campaignTypePresent: Record<number, boolean>,
      hasPrivateCampaign: boolean
    }) => {
      return {
        items: response.pagedResult.items.map((campaign) => ({
          id: campaign.id,
          imageSrc: this.getBlobUrl(campaign.blobUrl),
          heading: campaign.camapignTypeName,
          subHeading: campaign.campaignDisplayName,
          totalItems: campaign.totalItems,
          donatedItems: campaign.totalDonatedItems,
          availableItems: campaign.totalAvailableItems,
          isCompleted: campaign.isCampaignCompleted,
          bulkItemsInProgress: campaign.bulkItemsInProgress,
          typeId: campaign.camapignType,
          stringStartDate: campaign.stringStartDate,
          stringEndDate: campaign.stringEndDate,
          isPublic: campaign.isPublic,
          about: campaign.about,
          accessType: campaign.accessType,
          isFeatured: campaign.isFeatured,
          registrationSubType:campaign.registrationSubType
        })),
        paging: response.pagedResult.paging,
        campaignTypePresent: response.campaignTypePresent,
        hasPrivateCampaign: response.hasPrivateCampaign
      };
    }),
  );
}
  getNonArchivedCampaigns(communityId: number): Observable<ILinkedCampaignListDto[]> {
    const url = `campaign/list/${communityId}`;
    const options: IHttpRequestOptions = { url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response: ILinkedCampaignListDto[]) => response));
  }

  // Function to extract BlobUrl
  getBlobUrl(responseString: string): string {
    try {
      const response = JSON.parse(responseString);
      if (response?.length > 0) {
        return response[0].BlobUrl || null;
      } else {
        return '';
      }
    } catch (error) {
      console.error('Error parsing the response string:', error);
      return '';
    }
  }

  getCampaignTypeStandardForm(campaignTypeId : number) : Observable<IFormOverviewModel>{
    const url = `campaign/standard-form/campaigntype/${campaignTypeId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response:IFormOverviewModel) => response));
  }

  cloneCampaign(campaignId:number):Observable<boolean>{
      const url = `CampaignClone/${campaignId}`;
      const options: IHttpRequestOptions = { url: url };
      return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(
      map((response: any) => {
        return response?.success === true;
      }),
      catchError((error: any) => {
        console.error('Error cloning campaign:', error);
        return of(false);
      })
    );
  }

  getCampaignsTemplatesByVertical(
    verticalType: number,
  ): Observable<CampaignTemplateBase[]> {
    const url = `CampaignTemplate/vertical/${verticalType}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response: CampaignTemplateBase[]) => response));
  }

  getCampaignsTemplateById(
    templateId: number,
    communityVerticalId?: number
  ): Observable<CampaignTemplateModel> {
    const url = `CampaignTemplate/${templateId}/?verticalid=${communityVerticalId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response: CampaignTemplateModel) => response));
  }

  createCampaign(campaignDto: ICampaignDto): Observable<ICampaign> {
    const url = `Campaign`;
    const options: IHttpRequestOptions = {
      url: url,
      body: campaignDto,
    };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: ICampaign) => response));
  }

  createCampaignItems(campaignDto: ICampaignItemDto, isActivateCampaign: boolean = false): Observable<string> {
     const url = `CampaignItem?isActivateCampaign=${isActivateCampaign}`;
    const options: IHttpRequestOptions = {
      url: url,
      body: campaignDto,
    };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: string) => response));
  }

  updateCampaign(campaignDto: ICampaignBase): Observable<any> {
    const url = `Campaign`;
    const options: IHttpRequestOptions = {
      url: url,
      body: campaignDto,
    };
    return this.httpWrapperService
      .putFromApiGateWay(options)
      .pipe(map((response: any) => response));
  }

  getCampaignById(campaignId: number, isPrivatelyShared=false, skipTransferCache=false): Observable<ICampaignDetailModel | null> {
    const url = `Campaign/${campaignId}?isPrivatelyShared=${isPrivatelyShared}`;
    const options: IHttpRequestOptions = { url: url, ...(skipTransferCache ? { transferCache: false } : {}) };

    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(
        map((response: ICampaignDetailModel) => {
          return response; // Successful response
        }),
        catchError((error: any) => {
            return of(null);
        })
      );
  }
  getLinkedInitivatesByCampaignId(campaignId: number): Observable<ILinkedInitiativeModel[] | null> {
    const url = `InitiativeCampaignLinking/${campaignId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(
        map((response: ILinkedInitiativeModel[]) => response),
        catchError((error: any) => {
          console.error('Error fetching linked initiatives:', error);
          return of(null);
        })
      );
  }

  updateAccessStatus(campaignId: number, accessStatusId: number): Observable<boolean> {
    const url = `Campaign/access-status/${campaignId}/${accessStatusId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .patchFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  archiveCampaign(campaignId: number): Observable<boolean> {
    const url = `Campaign/archive/${campaignId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .patchFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  deleteCampaignAndItems(campaignId: number): Observable<boolean> {
    const url = `Campaign/${campaignId}/?isItemsDelete=true`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .deleteFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }
  getCampaignsUsers(campaignId: number): Observable<IContributor[]> {
    const url = `LinkedUserCampaign/${campaignId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response: IContributor[]) => response));
  }

  linkUnlinkCampaignUsers(
    campaignId: number,
    userIds: number[],
  ): Observable<boolean> {
    const url = `LinkedUserCampaign/${campaignId}`;
    const options: IHttpRequestOptions = {
      url: url,
      body: userIds,
    };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: boolean) => response));
  }

  getCampaignPromotionalContentPdf(campaignId: number, routeString: string): Observable<Blob> {
    const url = `PromotionalContent/${campaignId}`;
     const headers = new HttpHeaders().set('Content-Type', 'application/json');
     const options: IHttpRequestOptions = { 
      url: url,
      headers: headers,
      body: {data : routeString},
      responseType: 'blob'
  };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: Blob) => response));
  }

  getGroupedCampaignsByCommunityId(communityId: number, accessType: number): Observable<IGroupedCampaignItem[]> {
    const url = `Campaign/grouped/${communityId}/${accessType}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(
        map((response: IGroupedCampaignItem[]) => response),
        catchError((error: any) => {
          console.error('Error fetching grouped campaigns:', error);
          return of([]);
        })
      );
  }

  reorderCampaigns(communityId: number, reorderData: Array<{ campaignId: number; sortOrder: number; isFeatured: boolean }>): Observable<any> {
    const url = `Campaign/reorder/${communityId}`;
    const options: IHttpRequestOptions = { url: url, body: reorderData };
    return this.httpWrapperService
      .putFromApiGateWay(options)
      .pipe(
        map((response: any) => response),
        catchError((error: any) => {
          console.error('Error reordering campaigns:', error);
          return of(null);
        })
      );
  }

  getCampaignFormConfig(campaignTypeId: string): Observable<any> {
    const url = `CampaignFormConfig/${campaignTypeId}`;
    const options: IHttpRequestOptions = { url: url };
    return this.httpWrapperService
      .getFromApiGateWay(options)
      .pipe(map((response: any) => response));
  }

  generateAICampaign(campaignDto: any): Observable<any> {
    const url = `CampaignAi`;
    const options: IHttpRequestOptions = {
      url: url,
      body: campaignDto,
    };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: any) => response));
  }

  processAICampaign(aiCampaignId: number): Observable<any> {
    const options: IHttpRequestOptions = { url: `CampaignAi/${aiCampaignId}` };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: any) => response));
  }

  restoreAICampaign(campaignId: number): Observable<any> {
    const options: IHttpRequestOptions = { url: `RestoreAICampaign/${campaignId}` };
    return this.httpWrapperService
      .postFromApiGateWay(options)
      .pipe(map((response: any) => response));
  }
}
